import {
    ArrowPathIcon,
    HandThumbDownIcon,
    HandThumbUpIcon,
} from "@heroicons/react/24/outline";
import { ClientId, daysAgo, FeedbackType } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { InputFormBlock } from "components/app/input-form-block";
import { Header } from "components/header";
import { trpc } from "components/rpc/trpc";
import { Checkbox } from "components/ui/checkbox";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { TextArea } from "components/ui/textarea";
import React, { useCallback, useState } from "react";
import { handleEvent } from "util/user-events";

interface AiClientSummaryProps {
    clientId: ClientId;
}

const FEEDBACK_OPTIONS = [
    "Inaccurate information",
    "Missing important details",
    "Too long",
    "Too short",
    "Not relevant",
];

export const AiSummary: React.FC<AiClientSummaryProps> = ({ clientId }) => {
    const trpcClient = trpc.useTRPC();
    const [feedbackGiven, setFeedbackGiven] = useState<"positive" | "negative" | null>(
        null,
    );
    const [showFeedbackForm, setShowFeedbackForm] = useState(false);
    const [showRemoveButton, setShowRemoveButton] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const [feedbackText, setFeedbackText] = useState("");

    const {
        data: summary,
        isLoading: isQueryLoading,
        error: queryError,
        refetch: refetchSummary,
    } = useQuery({
        ...trpcClient.clientAiSummary.find.queryOptions({ clientId }),
    });

    const generateSummaryMutation = useMutation({
        ...trpcClient.clientAiSummary.generate.mutationOptions(),
        onSuccess: () => {
            setFeedbackGiven(null);
            setShowFeedbackForm(false);
            setShowRemoveButton(false);
            setSelectedOptions([]);
            setFeedbackText("");
            refetchSummary();
        },
    });

    const submitFeedbackMutation = useMutation({
        ...trpcClient.clientAiSummary.submitFeedback.mutationOptions(),
    });

    const removeSummaryMutation = useMutation({
        ...trpcClient.clientAiSummary.removeSummary.mutationOptions(),
        onSuccess: () => {
            setFeedbackGiven(null);
            setShowFeedbackForm(false);
            setShowRemoveButton(false);
            setSelectedOptions([]);
            setFeedbackText("");
            refetchSummary();
        },
    });

    const handleGenerateOrRegenerateSummary = useCallback(async () => {
        handleEvent("ai-summary-generate", {
            clientId,
            isRegenerate: !!summary,
        });
        generateSummaryMutation.mutate({
            clientId,
        });
    }, [clientId, generateSummaryMutation, summary]);

    const handleThumbsUp = useCallback(() => {
        if (!summary) return;
        setFeedbackGiven("positive");
        submitFeedbackMutation.mutate({
            summaryId: summary.id,
            feedbackType: FeedbackType.Positive,
        });
    }, [summary, submitFeedbackMutation]);

    const handleThumbsDown = useCallback(() => {
        if (!summary) return;
        setFeedbackGiven("negative");
        setShowFeedbackForm(true);
        setShowRemoveButton(true);
    }, [summary]);

    const handleToggleOption = useCallback((option: string) => {
        setSelectedOptions(prev =>
            prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option],
        );
    }, []);

    const handleSubmitFeedback = useCallback(() => {
        if (!summary) return;
        const combinedFeedback = [
            ...selectedOptions,
            feedbackText.trim() ? feedbackText.trim() : null,
        ]
            .filter(Boolean)
            .join("; ");

        submitFeedbackMutation.mutate({
            summaryId: summary.id,
            feedbackType: FeedbackType.Negative,
            feedbackText: combinedFeedback || undefined,
        });

        setShowFeedbackForm(false);
    }, [summary, selectedOptions, feedbackText, submitFeedbackMutation]);

    const handleRemoveSummary = useCallback(() => {
        if (!summary) return;
        removeSummaryMutation.mutate({
            summaryId: summary.id,
        });
    }, [summary, removeSummaryMutation]);

    const isLoading = isQueryLoading || generateSummaryMutation.isPending;
    const error = queryError?.message || generateSummaryMutation.error?.message;

    return (
        <InputFormBlock>
            <Header
                iconName="29_ClientSummary"
                action={
                    <SecondaryButton
                        disabled={isLoading}
                        onClick={handleGenerateOrRegenerateSummary}
                        className="flex items-center space-x-2">
                        {isLoading ? <Spinner /> : <ArrowPathIcon className="w-4 h-4" />}
                        {summary ? (
                            <span className="text-sm">Regenerate Summary</span>
                        ) : (
                            <span className="text-sm">Generate Summary</span>
                        )}
                    </SecondaryButton>
                }>
                AI Summary
            </Header>

            {error && (
                <div className="text-red-600 text-sm mb-4 p-3 bg-red-50 rounded-md">
                    {error}
                </div>
            )}

            {summary ? (
                <div className="space-y-2">
                    <div className="bg-white rounded-md">
                        <div className="text-gray-900 leading-relaxed whitespace-pre-wrap">
                            {summary.text}
                        </div>
                    </div>

                    <div className="flex items-start justify-between">
                        <p className="text-sm text-gray-500">
                            Generated {daysAgo(summary.dateAdded)}
                        </p>
                        <div className="flex flex-col items-end gap-2">
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleThumbsUp}
                                    disabled={submitFeedbackMutation.isPending}
                                    className={`p-1.5 rounded hover:bg-gray-100 transition-colors ${
                                        feedbackGiven === "positive"
                                            ? "text-green-600 bg-green-50"
                                            : "text-gray-400 hover:text-gray-600"
                                    }`}
                                    aria-label="This summary is helpful">
                                    <HandThumbUpIcon className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={handleThumbsDown}
                                    disabled={submitFeedbackMutation.isPending}
                                    className={`p-1.5 rounded hover:bg-gray-100 transition-colors ${
                                        feedbackGiven === "negative"
                                            ? "text-gray-700 bg-gray-100"
                                            : "text-gray-400 hover:text-gray-600"
                                    }`}
                                    aria-label="This summary needs improvement">
                                    <HandThumbDownIcon className="w-5 h-5" />
                                </button>
                            </div>
                            {showRemoveButton && (
                                <button
                                    onClick={handleRemoveSummary}
                                    disabled={removeSummaryMutation.isPending}
                                    className="text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50">
                                    {removeSummaryMutation.isPending
                                        ? "Removing..."
                                        : "Remove Summary"}
                                </button>
                            )}
                        </div>
                    </div>

                    {showFeedbackForm && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-md space-y-3 transition-all">
                            <p className="text-sm font-medium text-gray-700">
                                What could be improved?
                            </p>
                            <div className="space-y-2">
                                {FEEDBACK_OPTIONS.map(option => (
                                    <Checkbox
                                        key={option}
                                        label={option}
                                        checked={selectedOptions.includes(option)}
                                        onChange={() => handleToggleOption(option)}
                                    />
                                ))}
                            </div>
                            <TextArea
                                value={feedbackText}
                                onChange={e => setFeedbackText(e.target.value)}
                                placeholder="Additional feedback (optional)"
                                rows={3}
                                textSize="sm"
                            />
                            <PrimaryButton
                                onClick={handleSubmitFeedback}
                                disabled={submitFeedbackMutation.isPending}>
                                {submitFeedbackMutation.isPending
                                    ? "Sending..."
                                    : "Send Feedback"}
                            </PrimaryButton>
                        </div>
                    )}
                </div>
            ) : (
                <p className="text-gray-600 text-sm italic">
                    Generate an AI-powered summary of this client&apos;s information,
                    notes, and communications.
                </p>
            )}
        </InputFormBlock>
    );
};
